import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import Logo3D from '@/components/Logo3D';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthCallback({ setUser }) {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const hash = window.location.hash || '';
    const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
    const sessionId = params.get('session_id');

    if (!sessionId) {
      setError('No session found. Please try signing in again.');
      return;
    }

    (async () => {
      try {
        const resp = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        const { user, session_token } = resp.data;
        localStorage.setItem('dharmbook_token', session_token);
        localStorage.setItem('dharmbook_user', JSON.stringify(user));
        // Clear the hash so session_id isn't left in history
        window.history.replaceState(null, '', window.location.pathname);
        setUser(user);
        navigate('/', { replace: true });
      } catch (e) {
        setError(e.response?.data?.detail || 'Sign-in failed. Please try again.');
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-gray-50 via-orange-50 to-amber-50">
      <div className="text-center" data-testid="auth-callback-screen">
        <Logo3D size="xl" showText={true} />
        {error ? (
          <div className="mt-6">
            <p className="text-red-600 font-medium mb-4" data-testid="auth-callback-error">{error}</p>
            <button
              onClick={() => navigate('/auth', { replace: true })}
              className="px-6 py-2 rounded-full bg-[#FF9933] hover:bg-[#E68A00] text-white font-semibold"
              data-testid="auth-callback-back-btn"
            >
              Back to Login
            </button>
          </div>
        ) : (
          <div className="mt-6 flex items-center justify-center gap-2 text-gray-600">
            <Loader2 className="w-5 h-5 animate-spin text-[#FF9933]" />
            <span>Signing you in with Google...</span>
          </div>
        )}
      </div>
    </div>
  );
}
